import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { expand, map, Observable, of, reduce, takeWhile } from 'rxjs';
import {
  PaginatedResponse,
  PermitItemStatus,
  PermitKindCounter,
  PermitKindsResponse,
  PermitTask,
  PermitTaskBoardItem,
  PermitTaskKind,
  PermitTaskListOptions,
  PermitTaskWithTransitions
} from '../shared/interfaces';
import { CreatePermitTaskDTO, UpdatePermitTaskStatusDTO } from '../shared/schemas';
import { MAX_LIMIT } from '../shared/constants';
import { silentErrors, SKIP_ERROR_TOAST } from '../interceptors/error.interceptor';
import { API_URL } from '../tokens/api-url.token';

/**
 * Те, що деривація зробила за один прогін: зрушені статуси позицій і створені конвеєрні
 * завдання. Винесено окремим типом, бо ту саму пару — побайтово ту саму, camelCase
 * включно — віддає й `POST /permit-requests/:id/rederive`
 * (`PermitRequestService.rederive`, лист від 2026-09-23).
 *
 * З синку на неї нічого не приїде: у свагері `200` того маршруту описаний вбудованою
 * схемою, а `PermitItemStatusChange` живе в `packages/drizzle`, який до фронту не
 * копіюється. Тому тип оголошений тут, поруч із першим споживачем, а не в `core/shared`.
 *
 * **Порожній `changedItems` — це успіх, а не «нічого не сталося».** Типова відповідь на
 * застряглій заявці — саме порожній `changedItems` плюс непорожній `createdTasks`:
 * статуси вже правильні, бракувало самого завдання.
 */
export interface PermitDerivationResult {
  changedItems: { itemId: string; from: PermitItemStatus; to: PermitItemStatus }[];
  createdTasks: PermitTask[];
}

/**
 * Відповідь єдиної точки запису. Ширша за `{ permit_task }` навмисно: один перехід
 * може зрушити статус позицій, породити конвеєрне завдання і створити документ покупця.
 * Усі чотири поля застосовуються локально — спека прямо забороняє перезавантажувати дошку.
 */
export interface PatchPermitTaskResult extends PermitDerivationResult {
  permit_task: PermitTask;
  createdDocumentId: string | null;
}

export interface CreatePermitTaskResult {
  permit_task: PermitTask;
  /** Завдання створене, але рухати його нікому: на вид не призначено активного оператора. */
  warning?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermitTaskService {
  private http = inject(HttpClient);
  private apiUrl = `${inject(API_URL)}/permit-tasks`;

  /**
   * Ліміт сторінки дошки. Саме `MAX_LIMIT`, а не більше: сервер мовчки обрізає все, що
   * вище, і просити 200 означало б покладатись на те, що він чесно поверне обрізаний
   * `limit` у `pagination`. Він повертає — але добір сторінок від цього залежати не має.
   */
  private static readonly BOARD_PAGE_LIMIT = MAX_LIMIT;

  /**
   * Скоуп `request`: пагінації немає, список віддається цілком (спека, «два скоупи за одним
   * шляхом»). `kind` сюди не можна — схема `.strict()` перевіряє обидва напрямки, і зайвий
   * параметр дає `400` так само, як брак обов'язкового.
   *
   * Тиша — бо відмову картка пише рядком над позиціями: без переходів на ній немає кнопок
   * запису, і про це мусить сказати вона, а не тост поруч із тим самим текстом.
   */
  listForRequest(permitRequestId: string, departmentId?: string): Observable<PermitTaskWithTransitions[]> {
    let params = new HttpParams()
    .set('scope', 'request')
    .set('permit_request_id', permitRequestId);
    if (departmentId) params = params.set('department_id', departmentId);

    return this.http
    .get<{ data: PermitTaskWithTransitions[] }>(this.apiUrl, { params, context: silentErrors() })
    .pipe(map(response => response.data));
  }

  /**
   * Скоуп `operator`: рівно один `kind` за раз — машини різної довжини не об'єднуються.
   *
   * Тиша — бо відмову дошка оператора пише на місці колонок, з кнопкою повтору; інших викликів,
   * крім `listAllForOperator`, немає.
   */
  listForOperator(
    kind: PermitTaskKind,
    options: PermitTaskListOptions = {},
  ): Observable<PaginatedResponse<PermitTaskBoardItem>> {
    let params = new HttpParams()
    .set('scope', 'operator')
    .set('kind', kind)
    .set('page', (options.page ?? 1).toString())
    .set('limit', (options.limit ?? 20).toString());
    if (options.department_id) params = params.set('department_id', options.department_id);

    return this.http.get<PaginatedResponse<PermitTaskBoardItem>>(this.apiUrl, {
      params,
      context: silentErrors(),
    });
  }

  /**
   * Уся дошка одного виду, зібрана посторінково.
   *
   * Чому не «один запит на колонку», як у ремонтів: `listPermitTasksSchema` — `.strict()`, і
   * параметра `status` у ній НЕМАЄ (є лише `page`, `limit`, `scope`, `kind`,
   * `permit_request_id`, `department_id`). Фільтр за статусом на цьому маршруті просто дав би
   * `400`. Ремонти можуть інакше, бо в `RepairRequestListOptions` `status` є — дошки НЕ
   * симетричні, і копіювати сюди `listAll()` з ремонтів не можна.
   *
   * Тому статус розкладає клієнт (`bucketByStatus`), а сервер віддає всі завдання виду.
   * Ліміт узятий той самий, що й у ремонтів, і сторінки добираються до кінця: обрізана
   * дошка виглядає як повна й тим небезпечна.
   */
  listAllForOperator(kind: PermitTaskKind, departmentId?: string): Observable<PermitTaskBoardItem[]> {
    const fetch = (page: number) =>
      this.listForOperator(kind, {
        page,
        limit: PermitTaskService.BOARD_PAGE_LIMIT,
        department_id: departmentId,
      });

    let fetched = 0;
    return fetch(1).pipe(
      expand(response => {
        // Рахуємо ФАКТИЧНО отримані рядки, а не `page * limit`: `limit` — це те, що сервер
        // сказав про себе, і при обрізанні понад `MAX_LIMIT` воно розходиться з дійсністю.
        // Помилка тут не видна очима — дошка просто мовчки закінчується на першій сторінці.
        fetched += response.data.length;
        const exhausted = fetched >= response.pagination.total || response.data.length === 0;
        return exhausted ? of(null) : fetch(response.pagination.page + 1);
      }),
      takeWhile((response): response is PaginatedResponse<PermitTaskBoardItem> => response !== null),
      reduce((acc, response) => acc.concat(response.data), [] as PermitTaskBoardItem[]),
    );
  }

  /**
   * Конверт `/kinds` цілком, а не самий `data`: «не оператор жодного виду» приїжджає
   * полем `has_board_access`, і зрізати його тут означало б лишити виклик без єдиної
   * ознаки, що відрізняє «дошка не ваша» від «роботи немає». Порожній `data` буває в
   * обох станах, тож `data.length === 0` цього питання не вирішує.
   *
   * Тип конверта оголошено тут, а не в `core/shared/interfaces` — та тека належить
   * бекендові й приїжджає `copy-swagger`. Коли поле зʼявиться у синхронізованому DTO,
   * цей інтерфейс слід замінити на нього.
   *
   * Тост лишається вимкненим, але вже не через `403`: цей ендпоінт більше не відмовляє
   * (не бути оператором — нормальний стан більшості користувачів). Вимкнено його заради
   * мережевих збоїв — проб їде на кожне завантаження застосунку, і кричати про них
   * глобальним перехоплювачем нема за що. Хто кличе — той і показує стан.
   */
  getKinds(): Observable<PermitKindsResponse> {
    return this.http.get<PermitKindsResponse>(`${this.apiUrl}/kinds`, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }

  private readonly kindsState = signal<readonly PermitKindCounter[]>([]);
  private kindsProbed = false;

  /**
   * Види оператора для меню. Порожньо, доки проб не відповів, — і порожньо назавжди для
   * того, хто оператором не є.
   */
  readonly operatorKinds = this.kindsState.asReadonly();

  /**
   * Одноразовий проб `GET /permit-tasks/kinds` на застосунок.
   *
   * Меню оператора НЕ можна будувати зі списку дозволів (§3.4): `/permit-tasks/*` свідомо
   * не має `requirePermission`, доступ дає сам рядок у `permit_operators`. Продавця можна
   * призначити оператором, не змінюючи його ролі, — і тоді перевірка по дозволах
   * сховала б від нього єдину дошку з його ж роботою.
   */
  probeOperatorKinds(): void {
    if (this.kindsProbed) return;
    this.kindsProbed = true;

    this.getKinds().subscribe({
      next: response => this.kindsState.set(response.data),
      // Збій мережі лишає меню без пункту: показати дошку, до якої немає доступу, гірше,
      // ніж не показати її через збій. Стан «не оператор» сюди більше не потрапляє — він
      // приходить успішною відповіддю з порожнім `data`, і результат той самий.
      error: () => this.kindsState.set([]),
    });
  }

  /**
   * Тіло описує синхронізована `createPermitTaskSchema`, а не власний інтерфейс: саме вона
   * стоїть на вході бекенда, і своя копія розійшлася б із нею мовчки — зайве поле в
   * `.strict()` дає `400`, якого TypeScript не побачив би.
   *
   * Відповідь може нести `warning` — завдання створене, але рухати його нікому.
   *
   * Тиша — бо відмову показує діалог, українською й поруч із вибором: тост перехоплювача
   * сказав би те саме вдруге, англійським рядком сервера.
   */
  create(payload: CreatePermitTaskDTO): Observable<CreatePermitTaskResult> {
    return this.http.post<CreatePermitTaskResult>(this.apiUrl, payload, { context: silentErrors() });
  }

  /**
   * Єдина точка запису домену. Тіло — синхронізована `updatePermitTaskStatusSchema`:
   * `status` там рядок, а не enum по всіх видах, бо набір статусів **потиповий**
   * (`PermitTaskKindRegistry[kind].statuses`), а решта полів опційна, бо обов'язковість
   * залежить від пари (вид, цільовий статус) і приходить у `allowedTransitions[].requiredFields`.
   *
   * Відповідь ширша за `{ permit_task }` навмисно, і всі чотири поля треба застосувати
   * локально — спека прямо забороняє перезавантажувати дошку.
   *
   * Тиша — бо відмову пише діалог переходу: конфлікт номера зброї під полем, решту рядком угорі.
   * Тост перехоплювача повторював би її поверх модалки, а поки він живий, Escape діалог не
   * закриває.
   */
  patch(id: string, payload: UpdatePermitTaskStatusDTO): Observable<PatchPermitTaskResult> {
    return this.http.patch<PatchPermitTaskResult>(`${this.apiUrl}/${id}`, payload, {
      context: silentErrors(),
    });
  }
}
